import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, MapPin, User, Mail, Phone, Check, Plus, Trash2, Monitor, Volume2, Wifi } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_90b4c8a6-2372-4e9e-bd52-ac51039d9d6b/artifacts/l6uwsqcm_logo_bravo_cinemastreaming.png";

const normalize = (s) => (s || "").trim();

const isValidSpanishPostalCode = (cp) => /^\d{5}$/.test(normalize(cp));

const isValidCityOrProvince = (s) => {
  const v = normalize(s);
  return v.length >= 2; // mínimo 2 chars
};

// CIF/NIF/NIE básico (suficiente para la mayoría de casos)
const isValidCifNifNie = (value) => {
  const v = normalize(value).toUpperCase().replace(/\s+/g, "");

  // NIF: 8 dígitos + letra
  const nif = /^[0-9]{8}[A-Z]$/;
  // NIE: X/Y/Z + 7 dígitos + letra
  const nie = /^[XYZ][0-9]{7}[A-Z]$/;
  // CIF: letra + 7 dígitos + control (dígito o letra)
  const cif = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/;

  return nif.test(v) || nie.test(v) || cif.test(v);
};

const isNonEmpty = (s) => normalize(s).length > 0;


const CinemaRegisterPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Form Data with new structure
  const [formData, setFormData] = useState({
    // DATOS DE LA EMPRESA/ENTIDAD
    company_name: '', // Razón Social Completa
    cif: '', // CIF/NIF
    management_type: '', // 'publica' or 'privada'
    management_subtype: '', // Gestor Cultural, Agente Programador, Cine Independiente, Cadena
    fiscal_address: {
      street: '',
      city: '',
      postal_code: '',
      province: '',
      country: 'España'
    },
    
    // DATOS DE CONTACTO
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    password: '',
    confirmPassword: '',
    
    // UBICACIÓN RECINTO (array of venues)
    venues: [{
      venue_name: '',
      address: {
        street: '',
        city: '',
        postal_code: '',
        province: '',
        country: 'España'
      },
      rooms: [{
        room_number: '',
        seating_capacity: '',
        projection_system: 'DCP',
        sound_system: 'Dolby Digital 5.1',
        internet_connection: 'Fibra Óptica'
      }]
    }]
  });

  const projectionSystems = ['DCP', 'Digital HD', 'Digital 4K'];
  const soundSystems = ['Estéreo', 'Dolby Digital 5.1', 'Dolby Digital 7.1'];
  const internetConnections = ['ADSL', 'Fibra Óptica', '5G'];

  const managementOptions = {
    publica: ['Gestor Cultural', 'Agente Programador'],
    privada: ['Cine Independiente', 'Cadena', 'Agente Programador']
  };

  const updateFormData = (field, value) => {
    if (field.includes('.')) {
      const parts = field.split('.');
      if (parts.length === 2) {
        setFormData(prev => ({
          ...prev,
          [parts[0]]: {
            ...prev[parts[0]],
            [parts[1]]: value
          }
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const updateVenue = (venueIndex, field, value) => {
    setFormData(prev => {
      const newVenues = [...prev.venues];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        newVenues[venueIndex] = {
          ...newVenues[venueIndex],
          [parent]: {
            ...newVenues[venueIndex][parent],
            [child]: value
          }
        };
      } else {
        newVenues[venueIndex] = { ...newVenues[venueIndex], [field]: value };
      }
      return { ...prev, venues: newVenues };
    });
  };

  const updateRoom = (venueIndex, roomIndex, field, value) => {
    setFormData(prev => {
      const newVenues = [...prev.venues];
      const newRooms = [...newVenues[venueIndex].rooms];
      newRooms[roomIndex] = { ...newRooms[roomIndex], [field]: value };
      newVenues[venueIndex] = { ...newVenues[venueIndex], rooms: newRooms };
      return { ...prev, venues: newVenues };
    });
  };

  const addVenue = () => {
    setFormData(prev => ({
      ...prev,
      venues: [...prev.venues, {
        venue_name: '',
        address: { street: '', city: '', postal_code: '', province: '', country: 'España' },
        rooms: [{ room_number: '', seating_capacity: '', projection_system: 'DCP', sound_system: 'Dolby Digital 5.1', internet_connection: 'Fibra Óptica' }]
      }]
    }));
  };

  const removeVenue = (venueIndex) => {
    if (formData.venues.length > 1) {
      setFormData(prev => ({
        ...prev,
        venues: prev.venues.filter((_, i) => i !== venueIndex)
      }));
    }
  };

  const addRoom = (venueIndex) => {
    setFormData(prev => {
      const newVenues = [...prev.venues];
      newVenues[venueIndex] = {
        ...newVenues[venueIndex],
        rooms: [...newVenues[venueIndex].rooms, { room_number: '', seating_capacity: '', projection_system: 'DCP', sound_system: 'Dolby Digital 5.1', internet_connection: 'Fibra Óptica' }]
      };
      return { ...prev, venues: newVenues };
    });
  };

  const removeRoom = (venueIndex, roomIndex) => {
    if (formData.venues[venueIndex].rooms.length > 1) {
      setFormData(prev => {
        const newVenues = [...prev.venues];
        newVenues[venueIndex] = {
          ...newVenues[venueIndex],
          rooms: newVenues[venueIndex].rooms.filter((_, i) => i !== roomIndex)
        };
        return { ...prev, venues: newVenues };
      });
    }
  };

  const validatePassword = (password) => {
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    return hasMinLength && hasUppercase && hasLowercase && hasNumber;
  };

  const validateStep = (currentStep) => {
    const nextErrors = {};

    if (currentStep === 1) {
      if (!isNonEmpty(formData.company_name)) nextErrors.company_name = "Este campo es obligatorio";

      const cifMsg = validateCifNifNie(formData.cif);
      if (cifMsg) nextErrors.cif = cifMsg;

      if (!formData.management_type) nextErrors.management_type = "Selecciona Pública o Privada";
      if (formData.management_type && !formData.management_subtype) nextErrors.management_subtype = "Selecciona una opción";

      if (!isNonEmpty(formData.fiscal_address.street)) nextErrors["fiscal_address.street"] = "Dirección obligatoria";
      if (!isNonEmpty(formData.fiscal_address.city)) nextErrors["fiscal_address.city"] = "Ciudad obligatoria";

      const cp = (formData.fiscal_address.postal_code || "").trim();
      if (!/^\d{5}$/.test(cp)) nextErrors["fiscal_address.postal_code"] = "Código Postal debe tener 5 dígitos";

      if (!isNonEmpty(formData.fiscal_address.province)) nextErrors["fiscal_address.province"] = "Provincia obligatoria";
    }

    setFieldErrors(nextErrors);

    // marca touched todos los campos que fallan (para que se vean los mensajes)
    const touchedUpdates = {};
    Object.keys(nextErrors).forEach((k) => (touchedUpdates[k] = true));
    setTouched((prev) => ({ ...prev, ...touchedUpdates }));

    return Object.keys(nextErrors).length === 0;
  };

  const setTouchedField = (name) =>
    setTouched((prev) => ({ ...prev, [name]: true }));

  const setFieldError = (name, msg) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (msg) next[name] = msg;
      else delete next[name];
      return next;
    });
  };

  const getFieldError = (name) => {
    return touched[name] ? fieldErrors[name] : "";
  };

  const hasError = (name) => Boolean(getFieldError(name));

  const inputClassFor = (name) =>
    `${inputClass} ${hasError(name) ? "border-red-500 focus:border-red-500" : ""}`;

  const normalizeId = (v) =>
    (v || "")
      .toUpperCase()
      .replace(/[\s-]/g, "")
      .slice(0, 9);

  const DNI_LETTERS = "TRWAGMYFPDXBNJZSQVHLCKE";

  const isValidDNI = (value) => {
    // 8 dígitos + letra
    if (!/^\d{8}[A-Z]$/.test(value)) return false;
    const num = parseInt(value.slice(0, 8), 10);
    const letter = value[8];
    return DNI_LETTERS[num % 23] === letter;
  };

  const isValidNIE = (value) => {
    // X/Y/Z + 7 dígitos + letra
    if (!/^[XYZ]\d{7}[A-Z]$/.test(value)) return false;
    const map = { X: "0", Y: "1", Z: "2" };
    const num = parseInt(map[value[0]] + value.slice(1, 8), 10);
    const letter = value[8];
    return DNI_LETTERS[num % 23] === letter;
  };

  const isValidCIF = (value) => {
    // Letra + 7 dígitos + control (número o letra)
    if (!/^[ABCDEFGHJKLMNPQRSUVW]\d{7}[0-9A-J]$/.test(value)) return false;

    const letter = value[0];
    const digits = value.slice(1, 8); // 7
    const control = value[8];

    let sumEven = 0;
    let sumOdd = 0;

    for (let i = 0; i < digits.length; i++) {
      const n = parseInt(digits[i], 10);
      if (i % 2 === 0) {
        // posiciones impares (1,3,5,7) -> i=0,2,4,6
        const dbl = n * 2;
        sumOdd += Math.floor(dbl / 10) + (dbl % 10);
      } else {
        // pares
        sumEven += n;
      }
    }

    const sum = sumOdd + sumEven;
    const mod = sum % 10;
    const checkDigit = (10 - mod) % 10;

    const controlLetters = "JABCDEFGHI";
    const expectedDigit = String(checkDigit);
    const expectedLetter = controlLetters[checkDigit];

    // Según tipo de entidad, el control puede ser numérico, alfabético o cualquiera
    const forceDigit = "ABEH".includes(letter);
    const forceLetter = "KPQS".includes(letter);

    if (forceDigit) return control === expectedDigit;
    if (forceLetter) return control === expectedLetter;

    return control === expectedDigit || control === expectedLetter;
  };

  const validateCifNifNie = (raw) => {
    const v = normalizeId(raw);
    if (!v) return "Este campo es obligatorio";
    if (v.length !== 9) return "Debe tener 9 caracteres";
    if (isValidDNI(v) || isValidNIE(v) || isValidCIF(v)) return "";
    return "CIF/NIF/NIE no válido";
  };


  const handleSubmit = async () => {
    if (!validatePassword(formData.password)) {
      setError('La contraseña debe tener mínimo 8 caracteres, incluir mayúsculas, minúsculas y números');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Transform data for backend compatibility
      const submitData = {
        company_name: formData.company_name,
        cif: formData.cif,
        management_type: formData.management_type,
        management_subtype: formData.management_subtype,
        venue_name: formData.venues[0]?.venue_name || '',
        address: formData.venues[0]?.address || formData.fiscal_address,
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        contact_phone: formData.contact_phone,
        password: formData.password,
        fiscal_address: formData.fiscal_address,
        venues: formData.venues,
        technical_specs: {
          projection_system: formData.venues[0]?.rooms[0]?.projection_system || 'DCP',
          sound_system: formData.venues[0]?.rooms[0]?.sound_system || 'Dolby Digital 5.1',
          seating_capacity: parseInt(formData.venues[0]?.rooms[0]?.seating_capacity) || 0,
          internet_connection: formData.venues[0]?.rooms[0]?.internet_connection || 'Fibra Óptica'
        }
      };

      console.log('Submitting registration data:', submitData);

      const response = await fetch(`${BACKEND_URL}/api/cinemas/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      // Read response body once
      const responseText = await response.text();
      console.log('Response status:', response.status, 'Response:', responseText);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        data = null;
      }

      if (!response.ok) {
        // Translate common error messages to Spanish
        let errorMessage = data?.detail || 'Error al registrar el cine';
        if (errorMessage === 'Email already registered') {
          errorMessage = 'Este email ya está registrado. Por favor usa otro email.';
        }
        throw new Error(errorMessage);
      }

      setSuccess(true);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Error desconocido al registrar');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-white text-2xl font-bold mb-4">¡Registro completado!</h1>
          <p className="text-white/60 mb-8">
            Tu solicitud ha sido enviada correctamente. Nuestro equipo revisará tu información y te contactaremos pronto.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#e4002b] text-white font-medium rounded hover:bg-[#c70024] transition-colors"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const steps = [
    { id: 1, title: 'Empresa/Entidad', icon: Building2 },
    { id: 2, title: 'Contacto', icon: User },
    { id: 3, title: 'Ubicación Recinto', icon: MapPin }
  ];

  const inputClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-[#e4002b]";
  const selectClass = "w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#e4002b]";
  const labelClass = "block text-white/60 text-sm mb-2";

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-6">
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </Link>
          <img src={LOGO_URL} alt="Bravo Cinemastreaming" className="h-12 w-auto mb-4" />
          <h1 className="text-white text-3xl font-bold mb-2">Formulario de Registro</h1>
          <p className="text-white/60">
            Complete el formulario para dar de alta su sala y acceder a nuestro catálogo de Versión Digital.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-10">
          {steps.map((s, index) => (
            <div key={s.id} className="flex items-center">
              <div className={`flex flex-col items-center`}>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors ${
                  step >= s.id 
                    ? 'bg-[#e4002b] border-[#e4002b] text-white' 
                    : 'border-white/20 text-white/40'
                }`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <span className={`text-xs mt-2 ${step >= s.id ? 'text-white' : 'text-white/40'}`}>{s.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 md:w-32 h-0.5 mx-4 ${step > s.id ? 'bg-[#e4002b]' : 'bg-white/20'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="bg-[#141414] rounded-xl p-6 md:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: DATOS DE LA EMPRESA/ENTIDAD */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-white text-xl font-semibold mb-6 pb-2 border-b border-white/10">
                Datos de la Empresa/Entidad
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Razón Social Completa *</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => {
                      updateFormData("company_name", e.target.value);
                      if (touched.company_name) {
                        setFieldError("company_name", isNonEmpty(e.target.value) ? "" : "Este campo es obligatorio");
                      }
                    }}
                    onBlur={() => {
                      setTouchedField("company_name");
                      setFieldError("company_name", isNonEmpty(formData.company_name) ? "" : "Este campo es obligatorio");
                    }}
                    className={inputClassFor("company_name")}
                    placeholder="Nombre completo de la empresa"
                    required
                  />
                  {getFieldError("company_name") && (
                    <p className="text-red-400 text-xs mt-2">{getFieldError("company_name")}</p>
                  )}
                </div>
                <div>
                  <label className={labelClass}>CIF/NIF *</label>
                  <input
                    type="text"
                    inputMode="text"
                    autoCapitalize="characters"
                    value={formData.cif}
                    onChange={(e) => {
                      const normalized = normalizeId(e.target.value);
                      updateFormData("cif", normalized);

                      // valida si ya tocó el campo o ya tiene 9 caracteres
                      const msg = validateCifNifNie(normalized);
                      if (touched.cif || normalized.length === 9) setFieldError("cif", msg);
                    }}
                    onBlur={() => {
                      setTouchedField("cif");
                      setFieldError("cif", validateCifNifNie(formData.cif));
                    }}
                    maxLength={9}
                    className={inputClassFor("cif")}
                    placeholder="B12345678 / 12345678Z / X1234567L"
                    required
                  />

                  {getFieldError("cif") && (
                    <p className="text-red-400 text-xs mt-2">{getFieldError("cif")}</p>
                  )}
                </div>
              </div>

              {/* Gestión */}
              <div>
                <label className={labelClass}>Gestión *</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Pública */}
                  <div className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.management_type === 'publica' 
                      ? 'border-[#e4002b] bg-[#e4002b]/10' 
                      : 'border-white/10 hover:border-white/30'
                  }`} onClick={() => { updateFormData('management_type', 'publica'); updateFormData('management_subtype', ''); }}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${formData.management_type === 'publica' ? 'border-[#e4002b] bg-[#e4002b]' : 'border-white/40'}`} />
                      <span className="text-white font-medium">Pública</span>
                    </div>
                    {formData.management_type === 'publica' && (
                      <div className="ml-6 space-y-2">
                        {managementOptions.publica.map(opt => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="subtype"
                              checked={formData.management_subtype === opt}
                              onChange={() => updateFormData('management_subtype', opt)}
                              className="accent-[#e4002b]"
                            />
                            <span className="text-white/70 text-sm">{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Privada */}
                  <div className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.management_type === 'privada' 
                      ? 'border-[#e4002b] bg-[#e4002b]/10' 
                      : 'border-white/10 hover:border-white/30'
                  }`} onClick={() => { updateFormData('management_type', 'privada'); updateFormData('management_subtype', ''); }}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-4 h-4 rounded-full border-2 ${formData.management_type === 'privada' ? 'border-[#e4002b] bg-[#e4002b]' : 'border-white/40'}`} />
                      <span className="text-white font-medium">Privada</span>
                    </div>
                    {formData.management_type === 'privada' && (
                      <div className="ml-6 space-y-2">
                        {managementOptions.privada.map(opt => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="subtype"
                              checked={formData.management_subtype === opt}
                              onChange={() => updateFormData('management_subtype', opt)}
                              className="accent-[#e4002b]"
                            />
                            <span className="text-white/70 text-sm">{opt}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Domicilio Fiscal */}
              <div>
                <label className={labelClass}>Domicilio Fiscal</label>
                <div className="bg-white/5 rounded-lg p-4 space-y-4">
                  <div>
                    <label className="block text-white/50 text-xs mb-1">Dirección *</label>
                    <input
                      type="text"
                      value={formData.fiscal_address.street}
                      onChange={(e) => updateFormData('fiscal_address.street', e.target.value)}
                      className={inputClass}
                      placeholder="Calle, número, piso..."
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-white/50 text-xs mb-1">Ciudad *</label>
                      <input
                        type="text"
                        value={formData.fiscal_address.city}
                        onChange={(e) => updateFormData('fiscal_address.city', e.target.value)}
                        className={inputClass}
                        placeholder="Ciudad"
                      />
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs mb-1">Código Postal *</label>
                      <input
                        type="text"
                        value={formData.fiscal_address.postal_code}
                        onChange={(e) => {
                          const v = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
                          updateFormData("fiscal_address.postal_code", v);
                          if (touched["fiscal_address.postal_code"]) {
                            setFieldError(
                              "fiscal_address.postal_code",
                              /^\d{5}$/.test(v) ? "" : "Debe tener 5 dígitos"
                            );
                          }
                        }}
                        onBlur={() => {
                          setTouchedField("fiscal_address.postal_code");
                          const v = (formData.fiscal_address.postal_code || "").trim();
                          setFieldError(
                            "fiscal_address.postal_code",
                            /^\d{5}$/.test(v) ? "" : "Debe tener 5 dígitos"
                          );
                        }}
                        maxLength={5}
                        className={inputClassFor("fiscal_address.postal_code")}
                        placeholder="08000"
                      />

                      {getFieldError("fiscal_address.postal_code") && (
                        <p className="text-red-400 text-xs mt-2">
                          {getFieldError("fiscal_address.postal_code")}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs mb-1">Provincia *</label>
                      <input
                        type="text"
                        value={formData.fiscal_address.province}
                        onChange={(e) => updateFormData('fiscal_address.province', e.target.value)}
                        className={inputClass}
                        placeholder="Barcelona"
                      />
                    </div>
                    <div>
                      <label className="block text-white/50 text-xs mb-1">País</label>
                      <input
                        type="text"
                        value={formData.fiscal_address.country}
                        onChange={(e) => updateFormData('fiscal_address.country', e.target.value)}
                        className={inputClass}
                        placeholder="España"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: DATOS DE CONTACTO */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-white text-xl font-semibold mb-6 pb-2 border-b border-white/10">
                Datos de Contacto
              </h2>
              
              <div>
                <label className={labelClass}>Nombre de Contacto *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => updateFormData('contact_name', e.target.value)}
                    className={`${inputClass} pl-11`}
                    placeholder="Nombre y apellidos"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => updateFormData('contact_email', e.target.value)}
                      className={`${inputClass} pl-11`}
                      placeholder="email@ejemplo.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Teléfono *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => updateFormData('contact_phone', e.target.value)}
                      className={`${inputClass} pl-11`}
                      placeholder="+34 600 000 000"
                      required
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className={labelClass}>Contraseña *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  className={inputClass}
                  placeholder="Mínimo 8 caracteres"
                  required
                />
                <p className="text-white/40 text-xs mt-2">
                  Debe incluir mayúsculas, minúsculas y números
                </p>
                {formData.password && (
                  <div className="flex gap-2 mt-2">
                    <span className={`text-xs ${formData.password.length >= 8 ? 'text-green-400' : 'text-red-400'}`}>
                      {formData.password.length >= 8 ? '✓' : '✗'} 8+ caracteres
                    </span>
                    <span className={`text-xs ${/[A-Z]/.test(formData.password) ? 'text-green-400' : 'text-red-400'}`}>
                      {/[A-Z]/.test(formData.password) ? '✓' : '✗'} Mayúscula
                    </span>
                    <span className={`text-xs ${/[a-z]/.test(formData.password) ? 'text-green-400' : 'text-red-400'}`}>
                      {/[a-z]/.test(formData.password) ? '✓' : '✗'} Minúscula
                    </span>
                    <span className={`text-xs ${/[0-9]/.test(formData.password) ? 'text-green-400' : 'text-red-400'}`}>
                      {/[0-9]/.test(formData.password) ? '✓' : '✗'} Número
                    </span>
                  </div>
                )}
              </div>

              <div>
                <label className={labelClass}>Confirmar Contraseña *</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                  className={inputClass}
                  placeholder="Repita la contraseña"
                  required
                />
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-red-400 text-xs mt-2">Las contraseñas no coinciden</p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: UBICACIÓN RECINTO */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6 pb-2 border-b border-white/10">
                <h2 className="text-white text-xl font-semibold">Ubicación Recinto</h2>
                <button
                  type="button"
                  onClick={addVenue}
                  className="flex items-center gap-2 px-3 py-1.5 bg-[#e4002b]/20 text-[#e4002b] rounded-lg hover:bg-[#e4002b]/30 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" /> Añadir Recinto
                </button>
              </div>

              {formData.venues.map((venue, venueIndex) => (
                <div key={venueIndex} className="bg-white/5 rounded-xl p-5 space-y-5">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-medium">Recinto {venueIndex + 1}</h3>
                    {formData.venues.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVenue(venueIndex)}
                        className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" /> Eliminar
                      </button>
                    )}
                  </div>

                  {/* Venue Name and Address */}
                  <div>
                    <label className={labelClass}>Nombre del Recinto *</label>
                    <input
                      type="text"
                      value={venue.venue_name}
                      onChange={(e) => updateVenue(venueIndex, 'venue_name', e.target.value)}
                      className={inputClass}
                      placeholder="Nombre del cine o sala"
                      required
                    />
                  </div>

                  <div className="bg-white/5 rounded-lg p-4 space-y-3">
                    <p className="text-white/50 text-xs font-medium uppercase tracking-wider">Dirección del Recinto</p>
                    <div>
                      <input
                        type="text"
                        value={venue.address.street}
                        onChange={(e) => updateVenue(venueIndex, 'address.street', e.target.value)}
                        className={inputClass}
                        placeholder="Calle, número..."
                      />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <input
                        type="text"
                        value={venue.address.city}
                        onChange={(e) => updateVenue(venueIndex, 'address.city', e.target.value)}
                        className={inputClass}
                        placeholder="Ciudad"
                      />
                      <input
                        type="text"
                        value={venue.address.postal_code}
                        onChange={(e) => updateVenue(venueIndex, 'address.postal_code', e.target.value)}
                        className={inputClass}
                        placeholder="C.P."
                      />
                      <input
                        type="text"
                        value={venue.address.province}
                        onChange={(e) => updateVenue(venueIndex, 'address.province', e.target.value)}
                        className={inputClass}
                        placeholder="Provincia"
                      />
                      <input
                        type="text"
                        value={venue.address.country}
                        onChange={(e) => updateVenue(venueIndex, 'address.country', e.target.value)}
                        className={inputClass}
                        placeholder="País"
                      />
                    </div>
                  </div>

                  {/* Technical Data - Rooms */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-white/50 text-xs font-medium uppercase tracking-wider">Datos Técnicos - Salas</p>
                      <button
                        type="button"
                        onClick={() => addRoom(venueIndex)}
                        className="flex items-center gap-1 px-2 py-1 bg-white/10 text-white/70 rounded hover:bg-white/20 transition-colors text-xs"
                      >
                        <Plus className="w-3 h-3" /> Añadir Sala
                      </button>
                    </div>

                    <div className="space-y-3">
                      {venue.rooms.map((room, roomIndex) => (
                        <div key={roomIndex} className="bg-white/5 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-white/60 text-sm">Sala {roomIndex + 1}</span>
                            {venue.rooms.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRoom(venueIndex, roomIndex)}
                                className="text-red-400/70 hover:text-red-400 text-xs"
                              >
                                Eliminar
                              </button>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <div>
                              <label className="block text-white/40 text-xs mb-1">Nº Sala</label>
                              <input
                                type="number"
                                value={room.room_number}
                                onChange={(e) => updateRoom(venueIndex, roomIndex, 'room_number', e.target.value)}
                                className={inputClass}
                                placeholder="1"
                              />
                            </div>
                            <div>
                              <label className="block text-white/40 text-xs mb-1">Butacas</label>
                              <input
                                type="number"
                                value={room.seating_capacity}
                                onChange={(e) => updateRoom(venueIndex, roomIndex, 'seating_capacity', e.target.value)}
                                className={inputClass}
                                placeholder="150"
                              />
                            </div>
                            <div>
                              <label className="block text-white/40 text-xs mb-1 flex items-center gap-1">
                                <Monitor className="w-3 h-3" /> Proyector
                              </label>
                              <select
                                value={room.projection_system}
                                onChange={(e) => updateRoom(venueIndex, roomIndex, 'projection_system', e.target.value)}
                                className={selectClass}
                              >
                                {projectionSystems.map(sys => (
                                  <option key={sys} value={sys} className="bg-[#1a1a1a]">{sys}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-white/40 text-xs mb-1 flex items-center gap-1">
                                <Volume2 className="w-3 h-3" /> Sonido
                              </label>
                              <select
                                value={room.sound_system}
                                onChange={(e) => updateRoom(venueIndex, roomIndex, 'sound_system', e.target.value)}
                                className={selectClass}
                              >
                                {soundSystems.map(sys => (
                                  <option key={sys} value={sys} className="bg-[#1a1a1a]">{sys}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-white/40 text-xs mb-1 flex items-center gap-1">
                                <Wifi className="w-3 h-3" /> Internet
                              </label>
                              <select
                                value={room.internet_connection}
                                onChange={(e) => updateRoom(venueIndex, roomIndex, 'internet_connection', e.target.value)}
                                className={selectClass}
                              >
                                {internetConnections.map(conn => (
                                  <option key={conn} value={conn} className="bg-[#1a1a1a]">{conn}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            {step > 1 ? (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="px-6 py-3 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors"
              >
                Anterior
              </button>
            ) : (
              <div />
            )}
            
            {step < 3 ? (
              <button
                type="button"
                onClick={() => {
                  if (validateStep(step)) setStep(step + 1);
                }}
                className="px-6 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-colors"
              >
                Siguiente
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="px-8 py-3 bg-[#e4002b] text-white font-medium rounded-lg hover:bg-[#c70024] transition-colors disabled:opacity-50"
              >
                {loading ? 'Registrando...' : 'Completar registro'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CinemaRegisterPage;
